// ink-component
import React from 'react';
import { ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';

import getDirectionSymbol from './helpers/getDirectionSymbol';
import getNumberValue from '../_common/helpers/getNumberValue';
import getSpacing from './helpers/getSpacing';
import getValueFromParts from './helpers/getValueFromParts';
import getCurrencySign from './helpers/getCurrencySign';
import checkCurrencyDefaultPrecision from './helpers/checkCurrencyDefaultPrecision';
import {
  StyledCurrency,
  CurrencyDirectionSymbol,
  CurrencyMinusSign,
  CurrencySymbolPre,
  CurrencyParenthesesPre,
  CurrencyParenthesesPost,
  CurrencyValue,
  CurrencySymbolPost,
} from './styles';

import { LEFT, RIGHT, CURRENCY } from './constants';

export type FormatType = 'raw' | 'accounting' | 'gain' | 'loss' | 'gain-loss';

export interface CurrencyProps {
  /** Value to be formatted. */
  value?: number | string;
  /** The ISO-4217 currency code (USD, EUR, BRL, ...) */
  code?: string;
  /** Overrides the user browser's language-locale (window.navigator.language). You can pass: 'en-US', 'pt-BR', etc */
  locale?: string;
  /** Overrides the default display of the currency symbol. This allows you to use the full symbol, the currency code instead of the symbol, or the full name. */
  format?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  /** Sets the number of decimal places. Defaults to 0 if applied to a currency code that doesn't support fractions. */
  precision?: number;
  /** Sets the display format for the value */
  type?: FormatType;
  /** Replaces currency symbol with 3 letter currency code */
  explicit?: boolean;
  /** Sets the color of the component output to green (positive) / red (negative) */
  colorize?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  symbol?: string;
  /** Used in conjunction with the `code` prop to set different display formats for currencies. [Read more about primaryCurrency](`/components/Currency/usage`) */
  primaryCurrency?: string;
}

const getSSRSafeLocale = () => (typeof window !== 'undefined' ? window.navigator?.language : 'en-US');

const globalProps = getGlobalProps({});

/**
 * @purpose Formats currency in a standard way
 * @categories formatter
 * @aka money
 */
const Currency = (props: CurrencyProps) => {
  const {
    value,
    code,
    symbol = '$',
    locale,
    format = 'symbol',
    precision,
    type,
    explicit,
    colorize,
    'data-testid': dataTestId,
    primaryCurrency,
    ...otherProps
  } = props;

  const hasSymbol = symbol && symbol !== '$';
  let currencyFormat = format;

  if (primaryCurrency && code && !hasSymbol) {
    currencyFormat = primaryCurrency === code ? 'narrowSymbol' : 'code';
  }

  const MINUS_SIGN = String.fromCharCode(8722);

  const adjustedValue = getNumberValue(value);

  const userLocale = locale || getSSRSafeLocale();

  const shouldShowParentheses = adjustedValue
    ? adjustedValue < 0 && (type === 'accounting' || type === 'loss' || type === 'gain')
    : false;
  const isGainLoss = type === 'gain' || type === 'loss' || type === 'gain-loss';
  const shouldColorize = colorize;

  // We'll deprecate the 'symbol' prop. We're only keeping this here to prevent an immediate breaking change

  if (adjustedValue === undefined) {
    return (
      <ThemeProvider
        theme={currentTheme => ({
          ...currentTheme,
          undefined: adjustedValue === undefined,
        })}
      >
        <StyledCurrency {...globalProps} {...otherProps} data-testid={dataTestId} />
      </ThemeProvider>
    );
  }

  if (hasSymbol) {
    const formattedValue = new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
      currencyDisplay: currencyFormat,
    }).format(Math.abs(adjustedValue));

    return (
      <ThemeProvider
        theme={currentTheme => ({
          ...currentTheme,
          negative: shouldColorize && adjustedValue < 0 && (type === 'raw' || type === 'accounting'),
          positive: shouldColorize && adjustedValue > 0 && (type === 'raw' || type === 'accounting'),
          loss: shouldColorize && (type === 'loss' || (type === 'gain-loss' && adjustedValue < 0)),
          gain: shouldColorize && (type === 'gain' || (type === 'gain-loss' && adjustedValue > 0)),
          zero: shouldColorize && adjustedValue === 0,
        })}
      >
        <StyledCurrency {...globalProps} {...otherProps} data-testid={dataTestId}>
          {isGainLoss && adjustedValue !== 0 && (
            <CurrencyDirectionSymbol>{getDirectionSymbol(adjustedValue, type)}</CurrencyDirectionSymbol>
          )}
          {type === 'raw' && adjustedValue < 0 && <CurrencyMinusSign>{MINUS_SIGN}</CurrencyMinusSign>}
          <CurrencySymbolPre>{symbol}</CurrencySymbolPre>
          {shouldShowParentheses && <CurrencyParenthesesPre>(</CurrencyParenthesesPre>}
          <CurrencyValue>{formattedValue}</CurrencyValue>
          {shouldShowParentheses && <CurrencyParenthesesPost>)</CurrencyParenthesesPost>}
        </StyledCurrency>
      </ThemeProvider>
    );
  }

  const defaultPrecision = checkCurrencyDefaultPrecision(userLocale, code, currencyFormat);
  const explicitDisplay = explicit ? 'code' : format;

  const numberFormat = new Intl.NumberFormat(userLocale, {
    minimumFractionDigits: defaultPrecision === 0 ? 0 : precision,
    maximumFractionDigits: defaultPrecision === 0 ? 0 : precision,
    style: CURRENCY,
    currency: code,
    currencyDisplay: primaryCurrency ? currencyFormat : explicitDisplay,
  }).formatToParts(Math.abs(adjustedValue));
  const formatToParts = numberFormat;
  const { currencySign, position } = getCurrencySign(formatToParts);
  const hasSpacing = getSpacing(formatToParts, position);
  const formattedValue = getValueFromParts(formatToParts);

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        negative: shouldColorize && adjustedValue < 0 && (type === 'raw' || type === 'accounting'),
        positive: shouldColorize && adjustedValue > 0 && (type === 'raw' || type === 'accounting'),
        loss: shouldColorize && (type === 'loss' || (type === 'gain-loss' && adjustedValue < 0)),
        gain: shouldColorize && (type === 'gain' || (type === 'gain-loss' && adjustedValue > 0)),
        zero: shouldColorize && adjustedValue === 0,
      })}
    >
      <StyledCurrency {...globalProps} {...otherProps} data-testid={dataTestId}>
        {isGainLoss && adjustedValue !== 0 && (
          <CurrencyDirectionSymbol>{getDirectionSymbol(adjustedValue, type)}</CurrencyDirectionSymbol>
        )}
        {type === 'raw' && adjustedValue < 0 && <CurrencyMinusSign>{MINUS_SIGN}</CurrencyMinusSign>}
        {position === LEFT && <CurrencySymbolPre title={code}>{currencySign}</CurrencySymbolPre>}
        {position === LEFT && hasSpacing && ' '}
        {shouldShowParentheses && <CurrencyParenthesesPre>(</CurrencyParenthesesPre>}
        <CurrencyValue>{formattedValue}</CurrencyValue>
        {shouldShowParentheses && <CurrencyParenthesesPost>)</CurrencyParenthesesPost>}
        {position === RIGHT && hasSpacing && ' '}
        {position === RIGHT && <CurrencySymbolPost>{currencySign}</CurrencySymbolPost>}
      </StyledCurrency>
    </ThemeProvider>
  );
};

Currency.defaultProps = {
  value: undefined,
  symbol: undefined,
  code: 'USD',
  precision: 2,
  locale: undefined,
  type: 'accounting',
  explicit: false,
  style: undefined,
  format: 'symbol',
  primaryCurrency: undefined,
  'data-testid': undefined,
};

Currency.displayName = 'Currency';

export default Currency;
